import { useEffect, useRef, useState } from "react";
import { htmlToMarkdown, markdownToHtml } from "../../shared/markdown";
import { useDebouncedAutosave, type AutosaveStatus } from "../../shared/useDebouncedAutosave";
import { saveQmHandbuch } from "./api";

interface Props {
  handbuchContent: string;
  slug: string | null;
  onNext: (flush: () => Promise<void>) => void;
}

export function Step3Preview({ handbuchContent, slug, onNext }: Props) {
  const previewRef = useRef<HTMLDivElement>(null);
  const lastSeededRef = useRef<string>("");
  const [status, setStatus] = useState<{ kind: AutosaveStatus; message?: string }>({ kind: "idle" });

  const { schedule, flush } = useDebouncedAutosave({
    save: async () => {
      if (!slug || !previewRef.current) return;
      const md = htmlToMarkdown(previewRef.current.innerHTML);
      await saveQmHandbuch(slug, md);
    },
    onStatus: (kind, message) => {
      setStatus({ kind, message });
      if (kind === "saved") window.setTimeout(() => setStatus({ kind: "idle" }), 2000);
    },
  });

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (handbuchContent === lastSeededRef.current) return;
    lastSeededRef.current = handbuchContent;
    el.innerHTML = markdownToHtml(handbuchContent);
  }, [handbuchContent]);

  function statusText(): string {
    if (status.kind === "saving") return "Speichern…";
    if (status.kind === "saved") return "Gespeichert";
    if (status.kind === "error") return "Fehler: " + (status.message ?? "");
    return "";
  }

  return (
    <section className="wizard-section" data-qm-step="3">
      <h2 className="form-title">QM Handbuch: Prüfen &amp; bearbeiten</h2>
      <p className="form-description">
        Der Agent generiert das QM-Handbuch. Du kannst es direkt bearbeiten – Änderungen
        werden automatisch gespeichert.
      </p>
      <div className="form-actions" style={{ marginBottom: "1rem" }}>
        <span
          className={`autosave-status${status.kind === "saving" ? " saving" : ""}${status.kind === "saved" ? " saved" : ""}`}
          aria-live="polite"
        >
          {statusText()}
        </span>
      </div>
      <div
        ref={previewRef}
        className="result-preview result-preview-editable"
        contentEditable
        spellCheck
        suppressContentEditableWarning
        data-placeholder="QM-Handbuch wird generiert…"
        onInput={() => schedule()}
      />
      <div className="form-actions" style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onNext(flush)}
          disabled={!slug}
        >
          Weiter zum Download
        </button>
      </div>
    </section>
  );
}
